import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const AboutPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Navbar />
    <div className="pt-28 pb-20">
      {/* Hero */}
      <section className="relative h-72 mb-16 overflow-hidden">
        <img src="https://public.readdy.ai/ai/img_res/afb535ed7f212fc4ab27712d48479863.jpg" alt="About STRAY LOFT LLC" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-black/55 flex items-center">
          <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
            <p className="text-orange-400 text-sm font-semibold uppercase tracking-widest mb-2">Our Story</p>
            <h1 className="text-5xl font-black text-white">About STRAY LOFT LLC</h1>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Mission */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <p className="text-orange-500 text-sm font-semibold uppercase tracking-widest mb-3">Who We Are</p>
            <h2 className="text-4xl font-black text-gray-900 mb-5 leading-tight">Phoenix&apos;s Professional Hardware Source</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              STRAY LOFT LLC is a Phoenix, Arizona-based hardware tools retailer dedicated to supplying professional-grade equipment to contractors, tradespeople, and serious DIY enthusiasts across the United States.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              We believe every professional deserves access to quality tools without overpaying. That&apos;s why we carefully curate our catalog — 30 hand-picked products from industry-leading brands like DeWalt, Milwaukee, Bosch, Makita, Klein Tools, and more — all priced between $50 and $1,000.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Every product we carry is authentic, tested, and backed by manufacturer warranties. We stand behind what we sell, and our customers&apos; satisfaction is our top priority.
            </p>
            <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl border border-orange-100">
              <i className="ri-map-pin-2-line text-orange-500 text-2xl flex-shrink-0"></i>
              <div>
                <p className="font-semibold text-gray-800 text-sm">Our Location</p>
                <p className="text-gray-600 text-sm">3537 E Yale St, Phoenix, AZ 85008</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl overflow-hidden h-56 col-span-2">
              <img src="https://readdy.ai/api/search-image?query=professional%20hardware%20store%20interior%20organized%20tools%20display%20Phoenix%20Arizona%20modern%20retail%20clean%20bright&width=800&height=400&seq=about002&orientation=landscape" alt="Our Store" className="w-full h-full object-cover object-top" />
            </div>
            <div className="rounded-xl overflow-hidden h-40">
              <img src="https://readdy.ai/api/search-image?query=professional%20contractor%20using%20power%20tools%20construction%20site%20skilled%20worker%20Phoenix%20Arizona&width=400&height=320&seq=about003&orientation=portrait" alt="Professional Work" className="w-full h-full object-cover object-top" />
            </div>
            <div className="rounded-xl overflow-hidden h-40">
              <img src="https://readdy.ai/api/search-image?query=hardware%20tools%20quality%20inspection%20warehouse%20professional%20organized%20inventory&width=400&height=320&seq=about004&orientation=portrait" alt="Quality Tools" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <p className="text-orange-500 text-sm font-semibold uppercase tracking-widest mb-2">Our Values</p>
            <h2 className="text-4xl font-black text-gray-900">What We Stand For</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: "ri-medal-line", title: "Authentic Quality", desc: "Every product is 100% genuine, sourced directly from authorized distributors. No knockoffs, no compromises." },
              { icon: "ri-price-tag-3-line", title: "Fair Pricing", desc: "Professional-grade tools shouldn't break the bank. We price competitively so you can invest in your craft." },
              { icon: "ri-customer-service-2-line", title: "Customer First", desc: "From browsing to delivery, we're here to help. Reach us anytime at strayloftllc@outlook.com." },
            ].map((v) => (
              <div key={v.title} className="p-6 bg-gray-50 rounded-xl border border-gray-100 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-xl mx-auto mb-4">
                  <i className={`${v.icon} text-orange-500 text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{v.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="bg-gray-900 rounded-2xl p-10 mb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "30+", label: "Curated Products" },
              { value: "4.8★", label: "Average Rating" },
              { value: "3,200+", label: "Happy Customers" },
              { value: "100%", label: "Authentic Brands" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-4xl font-black text-orange-400 mb-1">{s.value}</div>
                <div className="text-gray-400 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Company images */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <p className="text-orange-500 text-sm font-semibold uppercase tracking-widest mb-2">Our Work</p>
            <h2 className="text-4xl font-black text-gray-900">Tools in Action</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { seq: "action001", q: "professional power drill in use construction framing house Phoenix Arizona contractor skilled work" },
              { seq: "action002", q: "professional circular saw cutting lumber construction site skilled carpenter work" },
              { seq: "action003", q: "professional electrician using multimeter electrical panel work skilled tradesman" },
              { seq: "action004", q: "professional plumber using pipe wrench plumbing work skilled contractor" },
              { seq: "action005", q: "professional carpenter using chisel woodworking furniture making skilled craftsman" },
              { seq: "action006", q: "professional contractor tool belt organized tools job site skilled worker" },
            ].map((img) => (
              <div key={img.seq} className="rounded-xl overflow-hidden h-48">
                <img
                  src={`https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28img.q%29%7D&width=600&height=400&seq=${img.seq}&orientation=landscape`}
                  alt="Tools in action"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-orange-50 rounded-2xl p-12 border border-orange-100">
          <h2 className="text-3xl font-black text-gray-900 mb-3">Ready to Shop?</h2>
          <p className="text-gray-600 mb-6">Browse our full catalog of 30 professional hardware tools</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/products" className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
              Shop All Products <i className="ri-arrow-right-line"></i>
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-3 border-2 border-gray-900 text-gray-900 font-bold rounded-lg hover:bg-gray-900 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default AboutPage;
